import React, { useState, useEffect } from "react";
import '../../styles/components/pages/auth.css';
import Button from 'react-bootstrap/Button';
import { authService } from '../../services/api';
import { useUser } from "../../contexts/UserContext";
import { userService } from "../../services/api";
import { useNavigate, useLocation } from 'react-router-dom';
import { useCourse } from '../../contexts/CourseContext';
import { toast } from 'react-toastify';

export default function ConsentPage() {

  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { openJoinModal } = useCourse();
  
  // Mark redirect as used to prevent dashboard auto-redirect while on consent
  useEffect(() => {
    try {
      sessionStorage.setItem('postLoginRedirectUsed', 'true');
      localStorage.setItem('postLoginRedirectUsed', 'true');
    } catch (_) {}
  }, []);

  const handleNoConsentGiven = () => {
    alert("Without consent, you do not have access to the rest of the site.");
  };

  const handleConsentGiven = async () => {
    try {

      const gtid = user?.loginID || user?.gtid || user?.username;
      

      // Use secure /auth/consent endpoint instead of /users/set-consent-given
      let response;
      try {
        console.log("Attempting POST /auth/consent (session-based)");
        response = await authService.giveConsent();
        console.log("Consent given:", response);
      } catch (err) {
        console.error("Primary consent endpoint failed, attempting fallback", err);
        // Fallback to legacy consent setter if backend supports it
        console.log("Falling back to POST /users/set-consent-given/" + gtid);
        if (!gtid) throw err;
        await userService.setConsentGiven(gtid);
        const qp = new URLSearchParams(location.search);
        const redirectArg = qp.get('redirect');
        response = { redirect: redirectArg || '/dashboard', fallback: true };
        console.log("Consent set via fallback endpoint");
      }

      // Set login flag for join modal
      try { sessionStorage.setItem('justLoggedIn', 'true'); } catch(e) {}

      // Refresh user data from /auth/me to get updated consent status
      const updatedUser = await authService.getCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
      }

      // Prefer explicit redirect query (e.g., /chatbot), then backend suggestion, else dashboard
      const qp = new URLSearchParams(location.search);
      const redirectArg = qp.get('redirect');
      const redirectPath = redirectArg || response?.redirect || '/dashboard';
      navigate(redirectPath);
    } catch (error) {
      console.error("Error updating consent:", error);
      toast.error("Failed to save consent. Please try again.");
    }
  };

  return(
    <div className="auth-container">
      <Button 
        className="auth-back-button"
        onClick={() => navigate('/')}>
        ← Back To Homepage
      </Button>

      <div className="auth-consent">
            <div className="auth-consent-heading">
              Consent to Participate
            </div>
            <div className="auth-consent-text">
            <p>
              You are being asked to volunteer in a research study aimed at improving learning outcomes in computer science courses through the use of an AI-powered learning environment. The purpose of this study is to assess typical challenges faced by novice coders and explore how AI-driven tools can enhance learning.
            </p>

            <p>
              To participate in this study, you must be enrolled in the CS 1301 course and physically located in the United States at the time of participation. Only students meeting these criteria are eligible to participate.
            </p>

            <p>
              Participation in the study involves using the learning environment to complete coding and tracing problems, which you may do at your own pace throughout the semester. You may also provide optional feedback on the relevance and helpfulness of the questions and recommended guidance. Your total time on the learning environment is entirely up to you, based on how much practice you choose to complete.
            </p>

            <p>
              All data collected from your participation, including coding submissions and feedback, will be de-identified before being used for research. While your name and login may be visible to your instructor, this identifying information will not be accessible to the research team. Instead, your data will be linked to a randomly generated, encrypted participant ID that cannot be used by the researchers to identify you. Data will be securely stored in an AWS database with industry-standard protections, including encryption and access control. Only authorized members of the research team will have access to the de-identified data.
            </p>

            <p>
              The risks involved in this study are no greater than those encountered in daily activities. While there are no direct personal benefits, you may receive extra credit for participating. Specifically, you will earn 0.2 points of extra credit for each completed problem set (e.g., homework assignment) within the learning environment, up to a maximum of 1.0 point added to your final numeric grade in the course. An alternative assignment offering equivalent extra credit will be available for students who choose not to participate, ensuring equal opportunity. Your decision to participate or not will not otherwise affect your standing or performance in the course.
            </p>

            <p>
              We will comply with all applicable laws and regulations regarding confidentiality. To ensure the research is conducted properly, study records may be reviewed by the Georgia Institute of Technology Institutional Review Board (IRB) or the Office for Human Research Protections (OHRP).
            </p>

            <p>
              If you have any questions about the study, you may contact Rodrigo Borela (PI) at rborelav@gatech.edu or Nimisha Roy (co-PI) at nroy9@gatech.edu. If you have questions about your rights as a research subject, you may contact the Georgia Institute of Technology Office of Research Integrity Assurance at IRB@gatech.edu.
            </p>

            <p>
              Thank you for considering participating in this study.
            </p>

            <p>
              By clicking "I consent," you indicate your consent to participate in the study.
            </p>
          </div>
        </div>
        <div className="auth-consent-buttons">
              <Button 
                className="auth-button auth-button-danger"
                onClick={handleNoConsentGiven}>
              I do not consent</Button>

              <Button 
                className="auth-button"
                onClick={handleConsentGiven}>
              I consent</Button>
        </div>
    </div>
  );
}